import React from 'react';
import PropTypes from 'prop-types';
import { MARKDOWN_TO_HTML } from '../../../common/custom/string-data';
import { getStopReasonMessage } from '../vibeCodingUtils';
import PlanProposal from './PlanProposal';
import BuildSteps from './BuildSteps';
import ChatInput from './ChatInput';
import { normalizePlanProposal } from '../utils/planUtils';

const ChatPanel = ({
  activeArtifact,
  currentPhase,
  canResumeApprovedPlan,
  isAwaitingPlanApproval,
  isHistoryLoading,
  isHistoryLoadingMore,
  hasMoreHistory,
  isPlanBusy,
  isResumable,
  isRunning,
  messages,
  messagesRef,
  milestones,
  onApprovePlan,
  onContinueBuilding,
  onPromptChange,
  onPromptKeyDown,
  onRequestPlanChanges,
  onSelectFile,
  onSend,
  onStop,
  onLoadOlder,
  onTogglePin,
  onToggleChat,
  onModelChange,
  onThinkingChange,
  selectedModel,
  selectedThinking,
  aiPinned,
  planProposal,
  prompt,
  stoppedInfo,
  streamingMessage,
}) => {
  const stopReasonLabel = getStopReasonMessage(stoppedInfo && stoppedInfo.reason);
  const normalizedPlanProposal = normalizePlanProposal(planProposal);
  const showPinnedPlanApproval =
    isAwaitingPlanApproval && Boolean(normalizedPlanProposal && normalizedPlanProposal.plan);
  const composerLocked = isRunning || isPlanBusy;
  const sendDisabled = !prompt.trim() || composerLocked || isAwaitingPlanApproval;
  const showProgress = isRunning && milestones && milestones.length > 0;
  const handleMessagesScroll = (event) => {
    const target = event.currentTarget;
    const reachedHistoryEnd = target.scrollTop <= 48;
    if (reachedHistoryEnd && hasMoreHistory && !isHistoryLoadingMore && onLoadOlder) {
      onLoadOlder();
    }
  };
  const canContinueInterruptedBuild =
    isResumable && !isRunning && !isPlanBusy && Boolean(onContinueBuilding);
  const composerPlaceholder = canResumeApprovedPlan
    ? 'Add build instructions…'
    : canContinueInterruptedBuild
    ? 'Add resume instructions…'
    : isAwaitingPlanApproval
    ? 'Describe requested changes…'
    : 'Ask DrapCode…';

  return (
    <div className="vibe-coding-chat">
      <div className="vibe-coding-chat__header">
        <span>DrapCode</span>
        <span className="vibe-coding-chat__header-actions">
          {onTogglePin ? (
            <button
              type="button"
              className={`dc-icon-btn dc-pin-toggle${aiPinned ? ' is-active' : ''}`}
              title={aiPinned ? 'Unpin panel' : 'Pin panel'}
              aria-label={aiPinned ? 'Unpin panel' : 'Pin panel'}
              aria-pressed={Boolean(aiPinned)}
              onClick={onTogglePin}
            >
              <i className="wb webconnect-pin-angle-fill" aria-hidden="true" />
            </button>
          ) : null}
          {onToggleChat ? (
            <button
              type="button"
              className="dc-icon-btn"
              title="Hide AI chat"
              aria-label="Hide AI chat"
              onClick={onToggleChat}
            >
              <i className="wb webconnect-layout-text-sidebar" aria-hidden="true" />
            </button>
          ) : null}
        </span>
      </div>

      <div className="vibe-coding-chat__body" ref={messagesRef} onScroll={handleMessagesScroll}>
        {normalizedPlanProposal && !showPinnedPlanApproval && (
          <div className="vibe-plan-dock">
            <PlanProposal
              proposal={normalizedPlanProposal}
              isBusy={isRunning || isPlanBusy}
              showActions={!isAwaitingPlanApproval}
              onApprove={onApprovePlan}
              onRequestChanges={onRequestPlanChanges}
            />
          </div>
        )}
        {isHistoryLoadingMore && (
          <div className="vibe-coding-chat__empty-state">Loading older messages...</div>
        )}

        {isResumable && !isRunning && (
          <div
            className="alert alert-info py-1 px-3 mx-3 mt-2 text-xs mb-0"
            style={{ fontSize: '11px' }}
          >
            {stoppedInfo && stoppedInfo.reason === 'task_turn_limit' ? (
              <>
                Task &quot;{stoppedInfo.currentTaskId || 'in progress'}&quot; paused after reaching
                the turn limit. Partial progress was saved — click Continue or send a message to
                resume.
              </>
            ) : stoppedInfo && stoppedInfo.reason === 'deadline' ? (
              <>
                The build reached the time limit. Partial progress was saved — click Continue to
                resume without replanning.
              </>
            ) : stoppedInfo && stoppedInfo.reason === 'connection_lost' ? (
              <>
                The connection was lost before the build finished. Partial progress was saved —
                click Continue to resume from where it left off.
              </>
            ) : (
              <>
                Generation was stopped
                {stopReasonLabel ? ` (${stopReasonLabel})` : ''}. Your partial progress was saved —
                click Continue or send another prompt to resume.
              </>
            )}
          </div>
        )}

        <div className="vibe-coding-chat__messages">
          {isHistoryLoading ? (
            <div className="vibe-coding-chat__empty-state">Loading conversation history...</div>
          ) : messages.length ? (
            messages.map((message) => {
              const role = message.role || 'agent';
              const content = message.content || '';
              const id = message.id || message.uuid || content;
              return (
                <div
                  className={`vibe-coding-chat__message vibe-coding-chat__message--${role}`}
                  key={id}
                >
                  <div className="vibe-coding-chat__message-label">
                    {role === 'agent' ? 'Agent' : 'You'}
                  </div>
                  <div
                    className="vibe-coding-chat__message-content"
                    dangerouslySetInnerHTML={{ __html: MARKDOWN_TO_HTML(content) }}
                  />
                </div>
              );
            })
          ) : (
            <div className="vibe-coding-chat__empty-state">
              <p className="font-weight-bold mb-1">What would you like to build?</p>
              <p className="text-muted">Describe your app to get started.</p>
            </div>
          )}

          {activeArtifact && (
            <div className="vibe-artifact-card">
              <div className="vibe-artifact-card__header">
                <span>⚡</span>
                <span>{activeArtifact.title || 'Code Artifact'}</span>
              </div>
              <div className="vibe-artifact-card__file-list">
                {(activeArtifact.files || []).map((file) => {
                  const filePath = file.filePath || '';
                  const status = file.status || 'writing';
                  return (
                    <button
                      type="button"
                      key={filePath}
                      className="vibe-artifact-card__file-item"
                      onClick={() => onSelectFile(filePath)}
                      title="View file in editor"
                    >
                      <span className="file-name">📄 {filePath}</span>
                      <span className={`file-badge file-badge--${status}`}>
                        {status === 'saved' ? '✓ saved' : 'writing...'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isRunning && streamingMessage && (
            <div className="vibe-coding-chat__message vibe-coding-chat__message--agent">
              <div className="vibe-coding-chat__message-label">Agent</div>
              <div
                className="vibe-coding-chat__message-content"
                dangerouslySetInnerHTML={{ __html: MARKDOWN_TO_HTML(streamingMessage) }}
              />
            </div>
          )}
        </div>
      </div>

      {isAwaitingPlanApproval && !isPlanBusy && (
        <div className="vibe-plan-approval-panel" role="region" aria-label="Plan approval">
          {showPinnedPlanApproval ? (
            <div className="vibe-plan-dock vibe-plan-dock--pinned">
              <PlanProposal
                proposal={normalizedPlanProposal}
                isBusy={isRunning || isPlanBusy}
                showActions={false}
                onApprove={onApprovePlan}
                onRequestChanges={onRequestPlanChanges}
              />
            </div>
          ) : (
            <div className="vibe-plan-approval-panel__missing">
              Plan details could not be loaded. Refresh the page or send a new plan request.
            </div>
          )}
          <div className="vibe-plan-actions-bar" aria-label="Plan approval actions">
            <button
              type="button"
              className="btn btn-theme-submit btn-sm"
              onClick={onApprovePlan}
              disabled={isRunning || isPlanBusy}
            >
              Approve &amp; Build
            </button>
            {onRequestPlanChanges && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={onRequestPlanChanges}
                disabled={isRunning || isPlanBusy}
              >
                Request changes
              </button>
            )}
          </div>
        </div>
      )}

      {canResumeApprovedPlan && !isAwaitingPlanApproval && !isPlanBusy && !isRunning && (
        <div
          className="vibe-plan-actions-bar vibe-plan-actions-bar--resume"
          role="region"
          aria-label="Resume build actions"
        >
          <span className="vibe-plan-actions-bar__hint">
            This plan was already approved. Continue from saved progress.
          </span>
          <button
            type="button"
            className="btn btn-theme-submit btn-sm"
            onClick={onApprovePlan}
            disabled={isRunning || isPlanBusy}
          >
            Continue
          </button>
        </div>
      )}

      {canContinueInterruptedBuild && !canResumeApprovedPlan && !isAwaitingPlanApproval && (
        <div
          className="vibe-plan-actions-bar vibe-plan-actions-bar--resume"
          role="region"
          aria-label="Resume interrupted build"
        >
          <span className="vibe-plan-actions-bar__hint">Build paused. Progress saved.</span>
          <button
            type="button"
            className="btn btn-theme-submit btn-sm"
            onClick={onContinueBuilding}
            disabled={isRunning || isPlanBusy}
          >
            Continue
          </button>
        </div>
      )}

      {showProgress && (
        <div className="px-3 pb-2">
          <BuildSteps milestones={milestones} phase={currentPhase} isRunning={isRunning} />
        </div>
      )}

      <div className="px-3 pb-3">
        <ChatInput
          prompt={prompt}
          isGenerating={isRunning}
          canSubmit={!sendDisabled}
          disabled={isPlanBusy}
          placeholder={composerPlaceholder}
          selectedModel={selectedModel}
          selectedThinking={selectedThinking}
          onModelChange={onModelChange}
          onThinkingChange={onThinkingChange}
          onChange={onPromptChange}
          onKeyDown={onPromptKeyDown}
          onSubmit={onSend}
          onStop={onStop}
        />
      </div>
    </div>
  );
};

ChatPanel.propTypes = {
  activeArtifact: PropTypes.shape({ files: PropTypes.array, title: PropTypes.string }),
  canResumeApprovedPlan: PropTypes.bool,
  currentPhase: PropTypes.object,
  isAwaitingPlanApproval: PropTypes.bool,
  isHistoryLoading: PropTypes.bool.isRequired,
  isHistoryLoadingMore: PropTypes.bool,
  hasMoreHistory: PropTypes.bool,
  isPlanBusy: PropTypes.bool,
  isResumable: PropTypes.bool,
  isRunning: PropTypes.bool.isRequired,
  messages: PropTypes.array.isRequired,
  messagesRef: PropTypes.object.isRequired,
  milestones: PropTypes.array,
  onApprovePlan: PropTypes.func,
  onContinueBuilding: PropTypes.func,
  onPromptChange: PropTypes.func.isRequired,
  onPromptKeyDown: PropTypes.func.isRequired,
  onRequestPlanChanges: PropTypes.func,
  onSelectFile: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onStop: PropTypes.func,
  onLoadOlder: PropTypes.func,
  onTogglePin: PropTypes.func,
  onToggleChat: PropTypes.func,
  onModelChange: PropTypes.func,
  onThinkingChange: PropTypes.func,
  selectedModel: PropTypes.string,
  selectedThinking: PropTypes.string,
  aiPinned: PropTypes.bool,
  planProposal: PropTypes.object,
  prompt: PropTypes.string.isRequired,
  stoppedInfo: PropTypes.object,
  streamingMessage: PropTypes.string.isRequired,
};

ChatPanel.defaultProps = {
  activeArtifact: null,
  canResumeApprovedPlan: false,
  currentPhase: null,
  isAwaitingPlanApproval: false,
  isHistoryLoadingMore: false,
  hasMoreHistory: false,
  isPlanBusy: false,
  isResumable: false,
  milestones: [],
  onApprovePlan: null,
  onContinueBuilding: null,
  onRequestPlanChanges: null,
  onStop: null,
  onLoadOlder: null,
  onTogglePin: null,
  onToggleChat: null,
  onModelChange: null,
  onThinkingChange: null,
  selectedModel: undefined,
  selectedThinking: undefined,
  aiPinned: false,
  planProposal: null,
  stoppedInfo: null,
};

export default ChatPanel;
